import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { setSession, type SessionUser } from '@/lib/session';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/accept-invite')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : '',
  }),
  component: AcceptInvitePage,
});

const ACCEPT_ERRORS: Record<string, string> = {
  invite_expired: 'This invite has expired or is invalid.',
  invite_consumed: 'This invite has already been used.',
  email_already_registered: 'That email address is already registered.',
  validation_failed: 'Please check your inputs and try again.',
};

function AcceptInvitePage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const inputClass =
    'rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring w-full';

  if (!token) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-destructive">Invalid invite link.</p>
      </main>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const result = await api.post<{ accessToken: string; user: SessionUser }>(
        '/v1/auth/invite/accept',
        { token, displayName, password },
      );
      setSession(result.accessToken, result.user);
      await navigate({ to: '/app' });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(ACCEPT_ERRORS[err.code] ?? err.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center">
      <form onSubmit={handleSubmit} className="flex w-full max-w-sm flex-col gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Create your account</h1>
          <p className="text-sm text-muted-foreground mt-1">You've been invited to join.</p>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <input
          type="text"
          placeholder="Your name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          required
          minLength={1}
          maxLength={100}
          className={inputClass}
        />
        <input
          type="password"
          placeholder="Choose a password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
          className={inputClass}
        />
        <Button type="submit" disabled={pending}>
          {pending ? 'Creating account…' : 'Create account'}
        </Button>
      </form>
    </main>
  );
}
